import { getHours, getMinutes, setHours, setMinutes } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import { isDesktop } from '@/core';
import { Calendar } from '@/ui/calendar';
import { DatetimeInput } from '@/ui/datetime-input';

type DatetimePopoverFieldProps = {
  id: string;
  label: string;
  value?: Date;
  onChange: (date: Date) => void;
  minDate?: Date;
  timeZone?: string;
  containerStyle?: any;
  // Lets the parent modal stop clipping (overflow: 'visible') while open.
  onOpenChange?: (isOpen: boolean) => void;
};

// Typed datetime field with a calendar popover. The popover is positioned
// against the nearest positioned ancestor (a form card or modal section), not
// the field itself, so it can be centered over the whole form.
export const DatetimePopoverField = ({
  id,
  label,
  value,
  onChange,
  minDate,
  timeZone,
  containerStyle,
  onOpenChange,
}: DatetimePopoverFieldProps) => {
  const { theme } = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const fieldRef = useRef<View>(null);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        fieldRef.current &&
        !(fieldRef.current as unknown as Node).contains(event.target as Node) &&
        isDesktop()
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <View
      ref={fieldRef}
      style={[{ position: 'static', zIndex: isOpen ? 10 : 0 }, containerStyle]}
    >
      <DatetimeInput
        label={label}
        value={value}
        minDate={minDate}
        timeZone={timeZone}
        onChange={onChange}
        onCalendarPress={() => {
          setIsOpen(true);
        }}
      />

      <View
        pointerEvents="box-none"
        style={{
          position: 'absolute',
          top: -40,
          left: 0,
          right: 0,
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <Calendar
          mode="datetime"
          isVisible={isOpen}
          headerTitle="Date"
          minDate={minDate}
          defaultStartDate={value}
          onHide={() => {
            setIsOpen(false);
          }}
          onDatesSelected={() => {}}
          onDateSelected={(date) => {
            const current = value || new Date();

            onChange(
              setMinutes(setHours(date, getHours(current)), getMinutes(current))
            );
          }}
          onTimeSelected={onChange}
          range={false}
          inline
          containerStyle={{
            borderWidth: 1,
            borderColor: theme.colors.card.default.borderColor,
            shadowColor: theme.colors.card.default.borderColor,
            shadowOffset: { width: 0, height: 5 },
            shadowOpacity: 0.5,
            shadowRadius: 5,
            elevation: 10,
          }}
          id={id}
        />
      </View>
    </View>
  );
};
